/**
 * Utility functions for handling prediction hiding logic
 * Predictions are hidden from other players until 1 hour before the game starts
 */

export interface PredictionHidingOptions {
  currentUserId?: string
  predictionOwnerId: string
  matchDate: string
}

/**
 * Check if a prediction should be hidden from other players
 * @param options - The options for checking prediction hiding
 * @returns true if prediction should be hidden, false otherwise
 */
export const shouldHidePrediction = (options: PredictionHidingOptions): boolean => {
  const { currentUserId, predictionOwnerId, matchDate } = options
  
  // If viewing own predictions, always show them
  if (currentUserId === predictionOwnerId) {
    return false
  }
  
  // For other players, check if game starts within 1 hour
  try {
    const matchDateTime = new Date(matchDate)
    const now = new Date()
    
    // Check if the date is valid
    if (isNaN(matchDateTime.getTime())) {
      console.warn('Invalid match date:', matchDate)
      return false // Don't hide if date is invalid
    }
    
    const oneHourBeforeMatch = new Date(matchDateTime.getTime() - 60 * 60 * 1000) // 1 hour in milliseconds

    return now < oneHourBeforeMatch
  } catch (error) {
    console.error('Error in shouldHidePrediction:', error)
    return false // Don't hide if there's an error
  }
}

type Translator = (key: string, options?: Record<string, string>) => string

/**
 * Format prediction score for display
 */
export const formatPredictionScore = (
  predictedScore: string,
  isHidden: boolean,
  t?: Translator
): string => {
  const translate = t ?? ((key: string) => key)
  if (isHidden) return `(${translate('stats.predicted')})`
  return predictedScore
}

/**
 * Format prediction result for display
 * Shows what the player PREDICTED (Victory or Draw), not the actual match result
 */
export const formatPredictionResult = (
  isHidden: boolean,
  predictedWinner?: string | null,
  t?: Translator
): string => {
  const translate = t ?? ((key: string) => key)
  if (isHidden) return `(${translate('stats.predicted')})`
  return predictedWinner ? `(${translate('stats.teamWins', { team: predictedWinner })})` : `(${translate('stats.draw')})`
}

